/**
 * 建 / 改「官网栏目」(1009 二期):站点 + 节奏(周几 + 时刻)+ 篇数或截止日期 + 默认写法 + 未审到点策略 + 每天上限。
 * 选题由 AI 滚动出未来 7 天(不扣费);发布前 48 小时自动建稿生成(按新闻舱现有口径扣费),生成完只到「待审」。
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { errorText } from "@/shared/lib/apiErrors";

import type { ReporterGenre } from "../api/reporterService";
import {
  reporterColumnService,
  type ColumnDetail,
  type ColumnRequest,
  type SiteColumn,
  type UnreviewedPolicy,
} from "../api/reporterColumnService";
import { reporterSiteService } from "../api/reporterSiteService";
import { GENRE_LABEL, TONE_LABEL, TONE_ORDER } from "../model/labels";
import { SITE_OPTIONS_KEY, siteLabel, siteOptionText } from "../model/sitePublication";

const GENRES: ReporterGenre[] = ["COMPANY_NEWS", "BRAND_PROFILE", "EVENT_RELEASE", "PRODUCT_COPY"];
const DAYS = [1, 2, 3, 4, 5, 6, 7];
const DAY_LABEL = ["", "一", "二", "三", "四", "五", "六", "日"];

const inputCls = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export function SiteColumnFormDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial?: SiteColumn | null;
  onClose: () => void;
  onSaved: (d: ColumnDetail) => void;
}) {
  const sitesQ = useQuery({
    queryKey: SITE_OPTIONS_KEY,
    queryFn: () => reporterSiteService.sites(),
  });
  const sites = sitesQ.data ?? [];
  const [siteId, setSiteId] = useState(initial?.siteId ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [weekdays, setWeekdays] = useState<number[]>(initial?.weekdays ?? [1, 3, 5]);
  const [time, setTime] = useState(initial?.publishTime ?? "09:00");
  const [limitBy, setLimitBy] = useState<"count" | "date">(
    initial?.endDate && initial.totalCount == null ? "date" : "count"
  );
  const [count, setCount] = useState(String(initial?.totalCount ?? 12));
  const [endDate, setEndDate] = useState(initial?.endDate ?? "");
  const [genre, setGenre] = useState(initial?.genre ?? "COMPANY_NEWS");
  const [tone, setTone] = useState(initial?.tone ?? "PROFESSIONAL");
  const [words, setWords] = useState(initial?.targetWords ? String(initial.targetWords) : "");
  const [useKnowledge, setUseKnowledge] = useState(initial?.useKnowledge ?? true);
  const [hint, setHint] = useState(initial?.topicHint ?? "");
  const [policy, setPolicy] = useState<UnreviewedPolicy>(initial?.unreviewedPolicy ?? "SKIP");
  const [dailyLimit, setDailyLimit] = useState(String(initial?.dailyLimit ?? 3));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSite = siteId || (sites.length === 1 ? sites[0].siteId : "");
  const toggleDay = (d: number) =>
    setWeekdays((w) =>
      w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort((a, b) => a - b)
    );

  const valid =
    !!effectiveSite &&
    name.trim().length > 0 &&
    weekdays.length > 0 &&
    /^\d{2}:\d{2}$/.test(time) &&
    (limitBy === "count" ? Number(count) >= 1 : !!endDate);

  const save = async () => {
    setBusy(true);
    setError(null);
    const req: ColumnRequest = {
      siteId: effectiveSite,
      name: name.trim(),
      weekdays,
      publishTime: time,
      totalCount: limitBy === "count" ? Number(count) : null,
      endDate: limitBy === "date" ? endDate : null,
      genre,
      tone,
      targetWords: words ? Number(words) : null,
      useKnowledge,
      topicHint: hint.trim() || null,
      unreviewedPolicy: policy,
      dailyLimit: Number(dailyLimit) || 3,
      enabled: initial ? initial.enabled : true,
    };
    try {
      const d = initial
        ? await reporterColumnService.update(initial.id, req)
        : await reporterColumnService.create(req);
      onSaved(d);
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-site-column-form="">
        <DialogHeader>
          <DialogTitle>{initial ? "改官网栏目" : "新建官网栏目"}</DialogTitle>
          <DialogDescription>
            AI 按节奏滚动出未来 7 天的选题(不扣积分);发布前 48
            小时自动写稿(按新闻舱写稿扣积分),写好先给你审,确认了才发。
          </DialogDescription>
        </DialogHeader>
        {sitesQ.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : sites.length === 0 && !initial ? (
          <p className="text-sm text-muted-foreground">
            还没有已上线的官网站点。先在建站舱建站并发布一次。
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            <label className="block space-y-1">
              <span className="text-xs font-semibold">站点</span>
              <select
                aria-label="栏目站点"
                className={inputCls}
                value={effectiveSite}
                disabled={!!initial}
                onChange={(e) => setSiteId(e.target.value)}
              >
                {sites.length > 1 && <option value="">请选择站点</option>}
                {initial && !sites.some((s) => s.siteId === initial.siteId) && (
                  <option value={initial.siteId}>
                    {siteLabel(initial.siteName, initial.siteUrl) || initial.siteId}
                  </option>
                )}
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteId}>
                    {siteOptionText(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">栏目名</span>
              <input
                aria-label="栏目名"
                className={inputCls}
                maxLength={64}
                value={name}
                placeholder="如:行业观察"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="space-y-1">
              <span className="text-xs font-semibold">节奏</span>
              <div className="flex flex-wrap items-center gap-1">
                {DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={weekdays.includes(d)}
                    className={`h-8 w-8 rounded-md border text-xs ${weekdays.includes(d) ? "border-primary bg-primary/10 text-primary" : "border-input"}`}
                    onClick={() => toggleDay(d)}
                  >
                    {DAY_LABEL[d]}
                  </button>
                ))}
                <input
                  type="time"
                  aria-label="发布时刻"
                  className="ml-2 h-8 rounded-md border border-input bg-background px-2"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  name="column-limit"
                  checked={limitBy === "count"}
                  onChange={() => setLimitBy("count")}
                />
                一共
              </label>
              <input
                type="number"
                aria-label="篇数"
                className="h-8 w-20 rounded-md border border-input bg-background px-2"
                min={1}
                max={365}
                value={count}
                disabled={limitBy !== "count"}
                onChange={(e) => setCount(e.target.value)}
              />
              篇
              <label className="ml-3 flex items-center gap-1">
                <input
                  type="radio"
                  name="column-limit"
                  checked={limitBy === "date"}
                  onChange={() => setLimitBy("date")}
                />
                发到
              </label>
              <input
                type="date"
                aria-label="截止日期"
                className="h-8 rounded-md border border-input bg-background px-2"
                value={endDate}
                disabled={limitBy !== "date"}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs font-semibold">体裁</span>
                <select
                  aria-label="体裁"
                  className={inputCls}
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                >
                  {GENRES.map((g) => (
                    <option key={g} value={g}>
                      {GENRE_LABEL[g]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold">语气</span>
                <select
                  aria-label="语气"
                  className={inputCls}
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                >
                  {TONE_ORDER.map((t) => (
                    <option key={t} value={t}>
                      {TONE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold">字数(空 = 按体裁默认)</span>
                <input
                  type="number"
                  aria-label="字数"
                  className={inputCls}
                  min={200}
                  max={5000}
                  value={words}
                  onChange={(e) => setWords(e.target.value)}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold">每天最多几篇(本站)</span>
                <input
                  type="number"
                  aria-label="每天上限"
                  className={inputCls}
                  min={1}
                  max={10}
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value)}
                />
              </label>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="用知识库"
                checked={useKnowledge}
                onChange={(e) => setUseKnowledge(e.target.checked)}
              />
              选题和写稿参考企业知识库
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold">选题方向(可选)</span>
              <input
                aria-label="选题方向"
                className={inputCls}
                maxLength={200}
                value={hint}
                placeholder="如:多写设备选型和售后服务"
                onChange={(e) => setHint(e.target.value)}
              />
            </label>
            <fieldset className="space-y-1">
              <legend className="text-xs font-semibold">到点还没审</legend>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="column-policy"
                  checked={policy === "SKIP"}
                  onChange={() => setPolicy("SKIP")}
                />
                这一期跳过(默认,排期里标「已跳过」)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="column-policy"
                  checked={policy === "POSTPONE"}
                  onChange={() => setPolicy("POSTPONE")}
                />
                顺延到下一个有空的日子
              </label>
              <p className="text-2xs text-muted-foreground">无论哪种,没审过的稿都不会发到官网。</p>
            </fieldset>
            {error && (
              <p className="text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button disabled={!valid || busy} onClick={() => void save()} data-site-column-save="">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initial ? "保存" : "建栏目"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
